import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { SeoHead } from '../components/SeoHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSiteSettings();

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news`);
        setNews(response.data);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="news-page">
      <SeoHead
        title="News | Aggiornamenti dall'Azienda Agricola del Vesuvio"
        description="Novità e aggiornamenti dall'Azienda Agricola Le Bontà del Vesuvio: stagioni di raccolta, nuovi prodotti, pomodorini napoletani, conserve artigianali campane e tradizioni vesuviane."
        keywords="news azienda agricola vesuvio, aggiornamenti prodotti campani, raccolta pomodorini vesuvio, novità conserve artigianali"
        canonical="/news"
      />

      {/* Hero */}
      <section className="relative flex items-center" data-testid="news-hero">
        <div className="absolute inset-0">
          {settings.news_hero_image_url ? (
            <img
              src={settings.news_hero_image_url}
              alt="News"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-b from-primary-dark to-primary" />
          )}
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
          <div className="relative container-custom pt-24 pb-6 md:pt-28 md:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-accent font-body text-xs uppercase tracking-widest mb-1 block">
              Novità dall'Azienda
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-white max-w-3xl">
              News e Aggiornamenti
            </h1>
            <p className="font-body text-sm text-white/80 mt-2 max-w-xl">
              Resta aggiornato sulle novità della nostra azienda, le stagioni di raccolta 
              e gli eventi speciali.
            </p>
          </motion.div>
        </div>
      </section>

      {/* News List */}
      <section className="section-spacing" data-testid="news-list">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="animate-pulse">
                  <div className="aspect-[16/9] bg-background-paper rounded-xl mb-4" />
                  <div className="h-4 bg-background-paper rounded w-1/4 mb-2" />
                  <div className="h-6 bg-background-paper rounded w-3/4 mb-2" />
                  <div className="h-4 bg-background-paper rounded w-full" />
                </div>
              ))}
            </div>
          ) : news.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-body text-text-muted text-lg mb-6">
                Nessuna notizia al momento. Torna presto per gli aggiornamenti!
              </p>
              <Link to="/">
                <Button className="bg-primary hover:bg-primary-dark text-white rounded-full px-8 py-6">
                  Torna alla Home
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
              {news.map((article, index) => (
                <motion.article
                  key={article.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group"
                  data-testid={`news-article-${article.id}`}
                >
                  <Link to={`/news/${article.id}`}>
                    <div className="relative aspect-[16/9] overflow-hidden rounded-lg sm:rounded-xl mb-3 sm:mb-6">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover img-zoom"
                      />
                    </div>
                    <div className="flex items-center gap-1.5 text-text-muted mb-1.5 sm:mb-3">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="font-body text-xs sm:text-sm">
                        {formatDate(article.created_at)}
                      </span>
                    </div>
                    <h2 className="font-heading text-sm sm:text-xl md:text-2xl font-semibold text-text-DEFAULT mb-1.5 sm:mb-3 group-hover:text-primary transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="hidden sm:block font-body text-text-muted leading-relaxed line-clamp-3">
                      {article.excerpt}
                    </p>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export const NewsDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${API}/news/${id}`);
        setArticle(response.data);
      } catch (error) {
        console.error('Error fetching article:', error);
        navigate('/news');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id, navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32">
        <div className="container-custom">
          <div className="animate-pulse max-w-3xl mx-auto">
            <div className="h-8 bg-background-paper rounded w-3/4 mb-4" />
            <div className="h-4 bg-background-paper rounded w-1/4 mb-8" />
            <div className="aspect-[16/9] bg-background-paper rounded-xl mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-background-paper rounded w-full" />
              <div className="h-4 bg-background-paper rounded w-full" />
              <div className="h-4 bg-background-paper rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) return null;

  return (
    <div data-testid="news-detail-page" className="min-h-screen">
      {/* Hero */}
      <section className="relative flex items-end">
        <div className="absolute inset-0">
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        
          <div className="relative container-custom pt-24 pb-6 md:pt-28 md:pb-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-body text-sm">Torna alle News</span>
            </Link>
            <div className="flex items-center gap-2 text-white/80 mb-4">
              <Calendar className="w-4 h-4" />
              <span className="font-body text-sm">
                {formatDate(article.created_at)}
              </span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              {article.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="section-spacing">
        <div className="container-custom">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-3xl mx-auto"
          >
            <div className="prose prose-lg max-w-none">
              <p className="font-body text-xl text-text-DEFAULT leading-relaxed mb-8">
                {article.excerpt}
              </p>
              <div className="font-body text-text-muted leading-relaxed whitespace-pre-line">
                {article.content}
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-primary/10">
              <p className="font-body text-text-muted mb-6">
                Vuoi saperne di più? Contattaci per qualsiasi informazione.
              </p>
              <a
                href="https://wa.me/393382523925?text=Ciao!%20Ho%20letto%20il%20vostro%20articolo%20e%20vorrei%20maggiori%20informazioni."
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-primary hover:bg-primary-dark text-white rounded-full px-8 py-6 font-body font-medium">
                  Contattaci su WhatsApp
                </Button>
              </a>
            </div>
          </motion.article>
        </div>
      </section>
    </div>
  );
};
